import React, { useEffect, useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { promptImageAltText } from './editorImageAlt';

/**
 * Inline alt editor shown when an image is selected in a TipTap editor.
 */
export function EditorImageAltControls({ editor }) {
  const [imageAlt, setImageAlt] = useState('');
  const imageActive = editor?.isActive('image') ?? false;

  useEffect(() => {
    if (!editor) return undefined;

    const syncFromSelection = () => {
      if (editor.isActive('image')) {
        setImageAlt(editor.getAttributes('image').alt ?? '');
      }
    };

    syncFromSelection();
    editor.on('selectionUpdate', syncFromSelection);
    editor.on('transaction', syncFromSelection);

    return () => {
      editor.off('selectionUpdate', syncFromSelection);
      editor.off('transaction', syncFromSelection);
    };
  }, [editor]);

  const applyAlt = (value) => {
    if (!editor?.isActive('image')) return;
    const alt = (value ?? imageAlt).trim();
    editor.chain().focus().updateAttributes('image', { alt }).run();
  };

  const handlePromptAlt = () => {
    if (!editor?.isActive('image')) return;
    const current = editor.getAttributes('image').alt ?? '';
    const alt = promptImageAltText(current);
    if (alt === null) return;
    setImageAlt(alt);
    applyAlt(alt);
  };

  if (!editor || !imageActive) return null;

  return (
    <div className="flex items-center gap-1.5 ml-1 pl-2 border-l border-gray-200 max-w-full">
      <label htmlFor="editor-image-alt" className="text-xs font-medium text-gray-600 whitespace-nowrap">
        Alt text
      </label>
      <Input
        id="editor-image-alt"
        className="h-7 w-36 sm:w-48 text-xs"
        value={imageAlt}
        onChange={(e) => setImageAlt(e.target.value)}
        onBlur={() => applyAlt()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            applyAlt();
          }
        }}
        placeholder="Describe image"
        title="Alt text for the selected image"
      />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 px-2 text-xs text-gray-600"
        onClick={handlePromptAlt}
        title="Edit alt text in dialog"
      >
        Edit
      </Button>
    </div>
  );
}

export default EditorImageAltControls;
